import type { I3BlogPost } from '../data/i3BlogPosts';

export type I3BlogFormat = 'daily' | 'devotional' | 'article';

export const I3_BLOG_FORMAT_LABELS: Record<I3BlogFormat, string> = {
  daily: 'Daily reflection',
  devotional: 'Devotional',
  article: 'Article',
};

/** Day-of-week and short-form journal series — filter / infer as daily. */
export const I3_DAILY_JOURNAL_TOPICS = [
  'Motivational Monday',
  'Mindset Monday',
  'Transparency Tuesday',
  'Transparent Tuesday',
  'Thoughtful Tuesday',
  'Thankful Tuesday',
  "Wednesday's Word",
  'Worry Free Wednesday',
  'Wisdom Wednesday',
  'Triumphant Thursday',
  'Transformation Thursday',
  'Focused Friday',
  'Freedom Friday',
  'Shackle Free Saturday',
  'Steadfast Saturday',
  'Standing on Business Saturday',
  'Sovereignty Sunday',
  'Sunday Shout',
] as const;

const ARTICLE_SLUGS = new Set([
  'welcome-to-our-journal',
  'small-habits-stronger-connection',
  'scheduled-post-example',
  'archived-post-example',
  'draft-not-visible',
  'building-trust-after-a-hard-season',
  'the-leader-you-want-to-be-under-pressure',
  'listening-like-it-matters',
  'money-talks-without-the-spiral',
  'parenting-as-a-united-front',
  'purpose-when-the-plan-changes',
  'retreat-weekend-what-to-expect',
  'boundaries-that-protect-love',
  'leading-through-uncertainty',
  'repair-after-a-harsh-word',
  'emotional-safety-at-work',
  'when-you-feel-alone-in-the-house',
  'faith-and-mental-health-together',
  'the-art-of-the-difficult-email',
  'raising-teens-with-firm-kindness',
  'your-calendar-is-a-moral-document',
  'holidays-without-the-scoreboard',
  'writing-a-personal-rule-of-life',
  'after-the-conference-high',
  'rest-as-a-leadership-strategy',
  'before-you-say-i-do-conversations',
  'stewardship-without-shame',
]);

const DEVOTIONAL_SLUGS = new Set([
  'complete-in-christ-2026-05-28',
  'how-to-get-high-2026-05-29',
  'head-heart-hands-godly-husband-pt-1-2026-06-13',
  'head-heart-hands-godly-husband-pt-2-2026-06-19',
  'how-to-get-paid-in-time-and-in-eternity-pt-3-2026-06-12',
  'stick-to-the-script-2026-07-24',
]);

function isDailyTopic(topic: string | undefined): boolean {
  const t = topic?.trim() ?? '';
  if (!t) return false;
  return I3_DAILY_JOURNAL_TOPICS.some((daily) => t === daily || t.startsWith(daily));
}

function hasBody(post: Pick<I3BlogPost, 'paragraphs'>): boolean {
  return post.paragraphs.some((p) => p.trim());
}

/** Resolve journal format — explicit CMS value wins, else infer from shape and topic. */
export function resolveBlogFormat(
  post: Pick<I3BlogPost, 'format' | 'quote' | 'meaning' | 'paragraphs' | 'topic' | 'slug'>
): I3BlogFormat {
  if (post.format === 'daily' || post.format === 'devotional' || post.format === 'article') {
    return post.format;
  }

  if (ARTICLE_SLUGS.has(post.slug)) return 'article';

  if (DEVOTIONAL_SLUGS.has(post.slug)) return 'devotional';

  if (isDailyTopic(post.topic)) return 'daily';

  if (hasBody(post)) return 'article';

  return 'daily';
}

export function isBlogFormat(value: unknown): value is I3BlogFormat {
  return value === 'daily' || value === 'devotional' || value === 'article';
}
